import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useDemandPartnersClient, demandPartnersListsQueryKey } from '../demand/partners/hooks';
import {
  DemandPartnerStatus,
  defaultDemandPartnersFilter,
  type DemandPartner,
} from '../demand/partners/model';
import { useIntegrationsClient, integrationsListsQueryKey } from '../demand/integrations/hooks';
import {
  defaultIntegrationsFilter,
  integrationStatusLabel,
  IntegrationStatus,
  type Integration,
} from '../demand/integrations/model';
import { usePlacementsClient, placementsListsQueryKey } from '../supply/placements/hooks';
import {
  defaultPlacementsFilter,
  PlacementStatus,
  resolvePlacementStatusLabel,
  type Placement,
} from '../supply/placements/model';
import { usePublisherClient, publishersListsQueryKey } from '../supply/publishers/hooks';
import {
  defaultPublisherListFilter,
  PublisherStatus,
  resolvePublisherStatusLabel,
  type Publisher,
} from '../supply/publishers/model';
import { isIdLikeSearchTerm, SearchEntityKind, type SearchResult } from './model';
import {
  demandPartnerSearchResult,
  integrationSearchResult,
  placementSearchResult,
  publisherSearchResult,
} from './searchResultRoutes';
import { useIdLookup } from './useIdLookup';

const SEARCH_RESULT_PAGE_SIZE = 5;
const MIN_SEARCH_TERM_LENGTH = 2;

export interface SearchResultGroup {
  kind: SearchEntityKind;
  results: SearchResult[];
}

function demandPartnerStatusLabel(status: DemandPartnerStatus) {
  return status.charAt(0) + status.slice(1).toLowerCase();
}

function toDisplayResults<TRow>(
  rows: readonly TRow[],
  mapRow: (row: TRow) => SearchResult,
  isActiveStatus: (row: TRow) => boolean,
  statusLabel: (row: TRow) => string,
  secondaryIdOf: (row: TRow) => string | null | undefined,
  term: string,
  idLike: boolean,
): SearchResult[] {
  const lowerTerm = term.toLowerCase();
  const mapped = rows.map((row) => {
    const base = mapRow(row);
    const statusSuffix = isActiveStatus(row) ? null : statusLabel(row);
    const secondaryId = secondaryIdOf(row);
    const matchesId =
      idLike &&
      (base.id.toLowerCase() === lowerTerm ||
        (typeof secondaryId === 'string' && secondaryId.toLowerCase() === lowerTerm));
    let sublabel = base.sublabel;
    if (matchesId) sublabel = statusSuffix ? `${base.id} · ${statusSuffix}` : base.id;
    else if (statusSuffix)
      sublabel = base.sublabel ? `${base.sublabel} · ${statusSuffix}` : statusSuffix;
    return { result: sublabel === base.sublabel ? base : { ...base, sublabel }, matchesId };
  });
  return mapped
    .slice()
    .sort((a, b) => Number(b.matchesId) - Number(a.matchesId))
    .map(({ result }) => result);
}

function mergeLookupHits(listResults: SearchResult[], lookupHits: readonly SearchResult[]) {
  const listIds = new Set(listResults.map((row) => row.id));
  return [...lookupHits.filter((hit) => !listIds.has(hit.id)), ...listResults];
}

export function useGlobalSearch(term: string, allowedKinds: ReadonlySet<SearchEntityKind>) {
  const trimmedTerm = term.trim();
  const isEnabled = trimmedTerm.length >= MIN_SEARCH_TERM_LENGTH;
  const idLike = isEnabled && isIdLikeSearchTerm(trimmedTerm);
  const publisherClient = usePublisherClient();
  const placementsClient = usePlacementsClient();
  const demandPartnersClient = useDemandPartnersClient();
  const integrationsClient = useIntegrationsClient();

  const publishersFilter = {
    ...defaultPublisherListFilter,
    statuses: Object.values(PublisherStatus),
    searchTerm: trimmedTerm,
    page: 0,
    size: SEARCH_RESULT_PAGE_SIZE,
  };
  const placementsFilter = {
    ...defaultPlacementsFilter,
    statuses: Object.values(PlacementStatus),
    searchTerm: trimmedTerm,
    page: 0,
    size: SEARCH_RESULT_PAGE_SIZE,
  };
  const demandPartnersFilter = {
    ...defaultDemandPartnersFilter,
    statuses: Object.values(DemandPartnerStatus),
    searchTerm: trimmedTerm,
    page: 0,
    size: SEARCH_RESULT_PAGE_SIZE,
  };
  const integrationsFilter = {
    ...defaultIntegrationsFilter,
    statuses: Object.values(IntegrationStatus),
    searchTerm: trimmedTerm,
    page: 0,
    size: SEARCH_RESULT_PAGE_SIZE,
  };

  const publishersQuery = useQuery({
    queryKey: [...publishersListsQueryKey, publishersFilter],
    queryFn: ({ signal }) => publisherClient.list(publishersFilter, { signal }),
    enabled: isEnabled && !idLike && allowedKinds.has(SearchEntityKind.Publisher),
  });
  const placementsQuery = useQuery({
    queryKey: [...placementsListsQueryKey, placementsFilter],
    queryFn: ({ signal }) => placementsClient.list(placementsFilter, { signal }),
    enabled: isEnabled && !idLike && allowedKinds.has(SearchEntityKind.Placement),
  });
  const demandPartnersQuery = useQuery({
    queryKey: [...demandPartnersListsQueryKey, demandPartnersFilter],
    queryFn: ({ signal }) => demandPartnersClient.list(demandPartnersFilter, { signal }),
    enabled: isEnabled && !idLike && allowedKinds.has(SearchEntityKind.DemandPartner),
  });
  const integrationsQuery = useQuery({
    queryKey: [...integrationsListsQueryKey, 'global', integrationsFilter],
    queryFn: ({ signal }) => integrationsClient.list(integrationsFilter, { signal }),
    enabled: isEnabled && !idLike && allowedKinds.has(SearchEntityKind.Integration),
  });

  const idLookup = useIdLookup(isEnabled ? trimmedTerm : '', allowedKinds);
  const lookupHitsByKind = useMemo(() => {
    const byKind = new Map<SearchEntityKind, SearchResult[]>();
    for (const hit of idLookup.results) {
      const hits = byKind.get(hit.kind) ?? [];
      hits.push(hit);
      byKind.set(hit.kind, hits);
    }
    return byKind;
  }, [idLookup.results]);

  const groups = useMemo<SearchResultGroup[]>(() => {
    if (idLike) {
      return [...allowedKinds].map((kind) => ({ kind, results: lookupHitsByKind.get(kind) ?? [] }));
    }
    const allGroups: SearchResultGroup[] = [
      {
        kind: SearchEntityKind.Publisher,
        results: mergeLookupHits(
          toDisplayResults<Publisher>(
            publishersQuery.data?.content ?? [],
            publisherSearchResult,
            (row) => row.status === PublisherStatus.Active,
            (row) => resolvePublisherStatusLabel(row.status),
            (row) => row.secondaryId,
            trimmedTerm,
            idLike,
          ),
          lookupHitsByKind.get(SearchEntityKind.Publisher) ?? [],
        ),
      },
      {
        kind: SearchEntityKind.Placement,
        results: mergeLookupHits(
          toDisplayResults<Placement>(
            placementsQuery.data?.content ?? [],
            placementSearchResult,
            (row) => row.status === PlacementStatus.Active,
            (row) => resolvePlacementStatusLabel(row.status),
            (row) => row.secondaryId,
            trimmedTerm,
            idLike,
          ),
          lookupHitsByKind.get(SearchEntityKind.Placement) ?? [],
        ),
      },
      {
        kind: SearchEntityKind.DemandPartner,
        results: mergeLookupHits(
          toDisplayResults<DemandPartner>(
            demandPartnersQuery.data?.content ?? [],
            demandPartnerSearchResult,
            (row) => row.status === DemandPartnerStatus.Active,
            (row) => demandPartnerStatusLabel(row.status),
            (row) => row.secondaryId,
            trimmedTerm,
            idLike,
          ),
          lookupHitsByKind.get(SearchEntityKind.DemandPartner) ?? [],
        ),
      },
      {
        kind: SearchEntityKind.Integration,
        results: mergeLookupHits(
          toDisplayResults<Integration>(
            integrationsQuery.data?.content ?? [],
            integrationSearchResult,
            (row) => row.status === IntegrationStatus.Active,
            (row) => integrationStatusLabel(row.status),
            () => undefined,
            trimmedTerm,
            idLike,
          ),
          lookupHitsByKind.get(SearchEntityKind.Integration) ?? [],
        ),
      },
    ];
    return allGroups.filter((group) => allowedKinds.has(group.kind));
  }, [
    publishersQuery.data,
    placementsQuery.data,
    demandPartnersQuery.data,
    integrationsQuery.data,
    lookupHitsByKind,
    trimmedTerm,
    idLike,
    allowedKinds,
  ]);

  return {
    groups,
    hasResults: groups.some((group) => group.results.length > 0),
    isLoading:
      isEnabled &&
      (publishersQuery.isLoading ||
        placementsQuery.isLoading ||
        demandPartnersQuery.isLoading ||
        integrationsQuery.isLoading ||
        idLookup.isLoading),
    showIdHint: idLike,
    idMatch: idLike ? idLookup.results[0] : undefined,
  };
}
